import { createRouter, createWebHistory } from 'vue-router'
import { useAppStore } from "@/store";
import { useTitle } from '@vueuse/core'
import { PERMISSIONS } from '@/config/roles';

const authGuard = async (to, from, next) => {
    const appStore = useAppStore();
    
    if (!appStore.session || Object.keys(appStore.session).length === 0) {
        await appStore.getUserSession();
    }

    if (appStore.isAuthenticated) {
        return next();
    } else {
        return next({ name: 'login' });
    }
};

function applyAuthGuard(routes, guard) {
    return routes.map(route => ({...route, beforeEnter: guard}));
}

const publicRoutes = [
    { path: '/', component: () => import('@/views/RedirectView.vue') },
    { path: '/403', name: '403', component: () => import('@/views/NoPermissionView.vue') },
    { path: '/login', name: 'login', component: () => import('@/views/LoginView.vue') },
    { path: '/otp', name: 'otp', component: () => import('@/views/LoginView.vue') },
    { path: '/logout', name: 'logout', component: () => import('@/views/LogoutView.vue') },
    { path: '/preview', name: 'preview', component: () => import('@/components/composer/builder/buildView.vue') },
    { path: '/display/:mac/:campaign_id?', name: 'display', component: () => import('@/components/display/DisplayView.vue') },
    { path: '/:pathMatch(.*)*', component: () => import('@/views/NotFoundView.vue') },
];

// Define protected routes with meta roles
const protectedRoutes = [
    {
        path: '/conferences',
        name: 'conferences',
        component: () => import('@/views/ConferencesView.vue'),
        meta: { roles: PERMISSIONS.conferences }
    },
    {
        path: '/scheduler',
        name: 'scheduler',
        component: () => import('@/views/SchedulerView.vue'),
        meta: { roles: PERMISSIONS.scheduler }
    },
    {
        path: '/presentations',
        name: 'presentations',
        component: () => import('@/views/PresentationsView.vue'),
        meta: { roles: PERMISSIONS.presentations }
    },
    {
        path: '/templates',
        name: 'templates',
        component: () => import('@/views/TemplatesView.vue'),
        meta: { roles: PERMISSIONS.templates }
    },
    {
        path: '/players',
        name: 'players',
        component: () => import('@/views/PlayersView.vue'),
        meta: { roles: PERMISSIONS.players }
    },
    {
        path: '/assets',
        name: 'assets',
        children: [
            {
                path: 'gallery',
                name: 'gallery',
                component: () => import('@/views/MediaView.vue'),
                meta: { roles: PERMISSIONS.media }
            },
            {
                path: 'playlists',
                name: 'playlists',
                component: () => import('@/views/PlaylistsView.vue'),
                meta: { roles: PERMISSIONS.media }
            }
        ]
    },
    {
        path: '/diagnostics',
        name: 'diagnostics',
        children: [
            {
                path: 'troubleshooting',
                name: 'troubleshooting',
                component: () => import('@/views/TroubleshootingView.vue'),
                meta: { roles: PERMISSIONS.diagnostics }
            },
            {
                path: 'logger',
                name: 'logger',
                component: () => import('@/views/LoggerView.vue'),
                meta: { roles: PERMISSIONS.diagnostics }
            },
            {
                path: 'alert-inbox',
                name: 'alert-inbox',
                component: () => import('@/views/AlertsInboxView.vue'),
                meta: { roles: PERMISSIONS.diagnostics }
            },
        ]
    },
    {
        path: '/software',
        name: 'software',
        children: [
            {
                path: 'management',
                name: 'management',
                component: () => import('@/views/ManagementView.vue'),
                meta: { roles: PERMISSIONS.software }
            },
            {
                path: 'devices',
                name: 'devices',
                component: () => import('@/views/DevicesView.vue'),
                meta: { roles: PERMISSIONS.software }
            }
        ]
    },
    {
        path: '/analytics',
        name: 'analytics',
        children: [
            {
                path: 'usage',
                name: 'usage',
                component: () => import('@/views/UsageView.vue'),
                meta: { roles: PERMISSIONS.analytics }
            }
        ]
    },
    {
        path: '/settings',
        name: 'settings',
        children: [
            {
                path: 'regional-settings',
                name: 'regional-settings',
                component: () => import('@/views/SettingsView.vue'),
                meta: { roles: PERMISSIONS.settings }
            },
            {
                path: 'alerts',
                name: 'alerts',
                component: () => import('@/views/AlertsView.vue'),
                meta: { roles: PERMISSIONS.diagnostics }
            },
            {
                path: 'operating-hours',
                name: 'operating-hours',
                component: () => import('@/views/OperatingHoursView.vue'),
                meta: { roles: PERMISSIONS.settings }
            },
        ]
    },
    {
        path: '/composer/:id',
        name: 'composer',
        component: () => import('@/components/composer/views/ComposerPage.vue'),
        props: true
    },
];

const guardedRoutes = applyAuthGuard(protectedRoutes, authGuard);
const routes = [...publicRoutes, ...guardedRoutes];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes
})

router.beforeEach(async(to, from, next) => {
    // Check page if is protected by roles
    if (!to.meta.roles || !useAppStore().tenant) return next()

    // await for appStore to get the role
    while (!useAppStore().getCurrentRole) await new Promise(resolve => setTimeout(resolve, 100));
    const roles = useAppStore().getCurrentRole;

    if (!to.meta.roles.includes(roles)) {
        // The user will get redirected to the first route he has access
        return next('/')
    }

    return next()
});

router.afterEach((to) => {
    const envTitle = process.env.VUE_APP_BASE_TITLE;
    const baseTitle = `Backoffice - ${envTitle || 'Nonius Signage'}`
    const pageTitle = to.meta.title || (to.name ? String(to.name).charAt(0).toUpperCase() + String(to.name).slice(1) : null)
    
    if (pageTitle) {
        useTitle(`${pageTitle} - ${baseTitle}`)
    } else {
        useTitle(baseTitle)
    }
})

export default router
